import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '@/hooks/useTheme'
import { BaseView, BaseText } from '@/components/base'
import { apyBottomSheetBg } from '@/assets/images'

interface BlurModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  showHandle?: boolean
  isFromColorfulScreen?: boolean
  className?: string
  style?: React.CSSProperties
}

export const BlurModal: React.FC<BlurModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showHandle = true,
  isFromColorfulScreen = false,
  className = '',
  style,
}) => {
  const theme = useTheme()

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Блокируем скролл body
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const modalContent = (
    <BaseView
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: 'opacity 0.3s ease-out',
      }}
    >
      {/* Backdrop - только затемнение как в RN */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Только затемнение без блюра
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexDirection: 'column',
        }}
        onClick={onClose}
      >
        {/* Modal Content - BottomSheet стиль */}
        <BaseView
          className={className}
          style={{
            width: '100%',
            maxHeight: '50vh',
            backgroundColor: isFromColorfulScreen
              ? 'transparent' // Прозрачный фон для картинки
              : theme.colors.background.cardItemRoot || '#18181A',
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            paddingTop: showHandle ? 12 : 24,
            paddingBottom: 24,
            paddingLeft: 24,
            paddingRight: 24,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
            transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            // Фоновая картинка для цветного экрана как в RN
            ...(isFromColorfulScreen && {
              backgroundImage: `url(${apyBottomSheetBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }),
            ...style,
          }}
          onClick={(e) => e.stopPropagation()} // Предотвращаем закрытие при клике на контент
        >
          {/* Handle - как в RN BottomSheet */}
          {showHandle && (
            <BaseView
              center
              style={{
                marginBottom: 24,
                paddingTop: 8,
                cursor: 'pointer',
              }}
              onClick={onClose}
            >
              <div
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: isFromColorfulScreen
                    ? 'rgba(255, 255, 255, 0.2)'
                    : theme.colors.label.disabled || '#5F6163',
                  borderRadius: 2,
                  opacity: 0.8,
                }}
              />
            </BaseView>
          )}

          {/* Title */}
          {title && (
            <BaseView center mb={24}>
              <BaseText
                fontSize={24}
                fontFamily="semibold"
                style={{
                  color: isFromColorfulScreen
                    ? 'white'
                    : theme.colors.label.default,
                  textAlign: 'center',
                }}
              >
                {title}
              </BaseText>
            </BaseView>
          )}

          {/* Content */}
          <BaseView
            style={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </BaseView>
        </BaseView>
      </div>
    </BaseView>
  )

  // Рендерим в портал для правильного z-index
  return createPortal(modalContent, document.body)
}
